import { config } from '../config';
import { User } from '../../types/api';
import { tokenManager } from '../auth/tokenManager';
import { WishlistItemUser } from '../../types/wishlist';

export interface PublicProfileResponse {
  id: number;
  friend_full_name: string;
  nickname?: string;
  birthday?: string;
  profile_picture?: number;
  wishlist: WishlistItemUser[];
}

export const profileApi = {
  getLocationSettings: async (userId: number): Promise<{ location: boolean }> => {
    const response = await tokenManager.makeAuthenticatedRequest(`${config.API_URL}/user/${userId}/settings/location`);

    if (!response.ok) {
      const data = await response.json();
      throw new Error(data.detail || data.message || response.statusText);
    }

    return response.json();
  },

  setLocationSettings: async (userId: number, locationEnabled: boolean): Promise<void> => {
    const response = await tokenManager.makeAuthenticatedRequest(`${config.API_URL}/user/${userId}/settings/location`, {
      method: 'POST',
      body: JSON.stringify({ location_bool: locationEnabled }),
    });

    if (!response.ok) {
      const data = await response.json();
      throw new Error(data.detail || data.message || response.statusText);
    }
  },

  updateField: async (userId: number, field: string, value: any): Promise<User> => {
    let requestBody;
    if (field === 'phone_number') {
      requestBody = { phone_number: value };
    } else if (field === 'birthday') {
      requestBody = { birthday: value };
    } else if (field === 'full_name') {
      requestBody = { full_name: value };
    } else if (field === 'nickname') {
      requestBody = { nickname: value };
    } else if (field === 'gender') {
      requestBody = { gender: value };
    } else if (field === 'bio') {
      requestBody = { bio: value };
    } else if (field === 'instagram') {
      requestBody = { instagram: value };
    } else if (field === 'facebook') {
      requestBody = { facebook: value };
    } else if (field === 'password') {
      requestBody = {
        password: value.password,
        new_password: value.new_password
      };
    } else if (field === 'profile_picture') {
      requestBody = { profile_picture: value };
    } else {
      requestBody = { value };
    }

    const response = await tokenManager.makeAuthenticatedRequest(`${config.API_URL}/user/update/${userId}/${field}`, {
      method: 'PATCH',
      body: JSON.stringify(requestBody),
    });

    if (!response.ok) {
      const data = await response.json();
      throw new Error(data.detail || data.message || response.statusText);
    }

    return response.json();
  },

  deactivate: async (userId: number, data: { password: string }): Promise<void> => {
    const response = await tokenManager.makeAuthenticatedRequest(`${config.API_URL}/user/${userId}/deactivate`, {
      method: 'POST',
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const data = await response.json();
      throw new Error(data.detail || data.message || response.statusText);
    }
  },

  delete: async (userId: number, data: { password: string }): Promise<void> => {
    const response = await tokenManager.makeAuthenticatedRequest(`${config.API_URL}/user/${userId}/delete`, {
      method: 'DELETE',
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const data = await response.json();
      throw new Error(data.detail || data.message || response.statusText);
    }
  },

  getPublicProfile: async (userId: number): Promise<PublicProfileResponse> => {
    const userResponse = await tokenManager.makeAuthenticatedRequest(`${config.API_URL}/user/public/${userId}`);

    if (!userResponse.ok) {
      const data = await userResponse.json();
      throw new Error(data.detail || data.message || userResponse.statusText);
    }

    const userData = await userResponse.json();
    
    // We don't need to fetch the wishlist data here anymore as it will be handled
    // by the wishlistApi.getAllPublicItems() call in Profile.tsx
    
    return {
      id: userId,
      friend_full_name: userData.full_name || 'Unknown',
      nickname: userData.nickname,
      profile_picture: userData.profile_picture,
      wishlist: [] // Empty array as a placeholder, will be populated from the Profile component
    };
  },
};
